import { useCallback, useState } from 'react'
import { useDropzone, type FileRejection } from 'react-dropzone'
import { Upload, X, Loader2, AlertCircle, Image as ImageIcon, Star, CheckCircle } from 'lucide-react'
import {
  useBuildingImages,
  useUploadBuildingImage,
  useDeleteBuildingImage,
  useReorderBuildingImages,
  type BuildingImage,
} from '@/hooks'
import { formatBytes, getCompressionRatio } from '@/lib/imageCompression'

// ============================================================================
// Constants
// ============================================================================

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB (before compression)
const ACCEPTED_MIME_TYPES = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/webp': ['.webp'],
  'image/avif': ['.avif'],
}

// ============================================================================
// Helper Functions
// ============================================================================

function getErrorMessage(rejection: FileRejection): string {
  const error = rejection.errors[0]
  if (error.code === 'file-too-large') {
    return `El archivo es muy grande (max. ${formatBytes(MAX_FILE_SIZE)})`
  }
  if (error.code === 'file-invalid-type') {
    return 'Tipo de archivo invalido. Solo se permiten JPEG, PNG, WebP y AVIF.'
  }
  return error.message
}

// ============================================================================
// Props Interface
// ============================================================================

interface BuildingImageUploaderProps {
  /** Building ID to upload images to */
  buildingId: string
  /** Whether the uploader is disabled */
  disabled?: boolean
}

// ============================================================================
// Component
// ============================================================================

export function BuildingImageUploader({ buildingId, disabled = false }: BuildingImageUploaderProps) {
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [settingPrimaryId, setSettingPrimaryId] = useState<string | null>(null)

  // Queries and mutations
  const { data: images = [], isLoading: isLoadingImages } = useBuildingImages(buildingId)
  const uploadMutation = useUploadBuildingImage()
  const deleteMutation = useDeleteBuildingImage()
  const reorderMutation = useReorderBuildingImages()

  // Handle file drop
  const onDrop = useCallback(
    async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      setUploadError(null)

      // Handle rejected files
      if (rejectedFiles.length > 0) {
        const errorMessages = rejectedFiles.map(
          (rejection) => `${rejection.file.name}: ${getErrorMessage(rejection)}`
        )
        setUploadError(errorMessages.join('; '))
        return
      }

      // Upload all accepted files
      for (const file of acceptedFiles) {
        try {
          await uploadMutation.mutateAsync({
            file,
            buildingId,
          })
        } catch (error) {
          setUploadError(
            error instanceof Error ? error.message : 'Error al subir imagen'
          )
          break // Stop on first error
        }
      }
    },
    [buildingId, uploadMutation]
  )

  // Configure dropzone
  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragReject,
  } = useDropzone({
    onDrop,
    accept: ACCEPTED_MIME_TYPES,
    maxSize: MAX_FILE_SIZE,
    disabled: disabled || uploadMutation.isPending,
    multiple: true,
  })

  // Handle delete
  const handleDelete = async (image: BuildingImage) => {
    if (!confirm('Estas seguro de que quieres eliminar esta imagen?')) return

    try {
      await deleteMutation.mutateAsync({
        id: image.id,
        buildingId,
      })
    } catch (error) {
      setUploadError(
        error instanceof Error ? error.message : 'Error al eliminar imagen'
      )
    }
  }

  // Handle set primary - moves the image to position 0
  const handleSetPrimary = async (image: BuildingImage) => {
    if (image.is_primary) return // Already primary

    setSettingPrimaryId(image.id)
    try {
      // Move this image to first position, keep others in order
      const otherImages = images.filter((img: BuildingImage) => img.id !== image.id)
      const newOrder = [image.id, ...otherImages.map((img: BuildingImage) => img.id)]

      await reorderMutation.mutateAsync({
        buildingId,
        imageIds: newOrder,
      })
    } catch (error) {
      setUploadError(
        error instanceof Error ? error.message : 'Error al establecer imagen principal'
      )
    } finally {
      setSettingPrimaryId(null)
    }
  }

  const isUploading = uploadMutation.isPending
  const isCompressing = uploadMutation.isCompressing
  const isDeleting = deleteMutation.isPending
  const isSettingPrimary = settingPrimaryId !== null
  const isBusy = isUploading || isDeleting || isSettingPrimary
  const compressionProgress = uploadMutation.compressionProgress

  return (
    <section className="rounded-lg border bg-card p-6">
      <h2 className="mb-4 text-lg font-semibold">Imagenes del Edificio</h2>

      {/* Error Display */}
      {uploadError && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div>
            <p className="font-medium">Error de Subida</p>
            <p>{uploadError}</p>
          </div>
          <button
            type="button"
            onClick={() => setUploadError(null)}
            className="ml-auto flex-shrink-0 p-1 hover:opacity-70"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Dropzone */}
      <div
        {...getRootProps()}
        className={`
          relative cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition-colors
          ${isDragActive && !isDragReject ? 'border-primary bg-primary/5' : ''}
          ${isDragReject ? 'border-red-500 bg-red-50 dark:bg-red-900/20' : ''}
          ${!isDragActive && !disabled ? 'border-muted-foreground/25 hover:border-muted-foreground/50' : ''}
          ${disabled || isBusy ? 'cursor-not-allowed opacity-50' : ''}
        `}
      >
        <input {...getInputProps()} />

        {isUploading ? (
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">
              {isCompressing ? 'Comprimiendo...' : 'Subiendo...'}
            </p>
            {compressionProgress && (
              <div className="flex items-center gap-2 text-xs text-green-600 dark:text-green-400">
                <CheckCircle className="h-4 w-4" />
                Comprimido: {formatBytes(compressionProgress.originalSize)} → {formatBytes(compressionProgress.compressedSize)}
                <span className="text-green-700 dark:text-green-300">
                  (-{getCompressionRatio(compressionProgress.originalSize, compressionProgress.compressedSize)})
                </span>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Upload className="h-10 w-10 text-muted-foreground" />
            <div>
              <p className="font-medium">
                {isDragActive
                  ? isDragReject
                    ? 'Tipo de archivo invalido'
                    : 'Suelta las imagenes aqui'
                  : 'Arrastra y suelta imagenes aqui'}
              </p>
              <p className="text-sm text-muted-foreground">
                o haz clic para seleccionar archivos
              </p>
            </div>
            <p className="text-xs text-muted-foreground">
              JPEG, PNG, WebP o AVIF hasta {formatBytes(MAX_FILE_SIZE)} (se optimizaran automaticamente)
            </p>
          </div>
        )}
      </div>

      {/* Images Grid */}
      {isLoadingImages ? (
        <div className="mt-6 flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : images.length > 0 ? (
        <div className="mt-6">
          <h3 className="mb-3 text-sm font-medium text-muted-foreground">
            Imagenes Subidas ({images.length})
          </h3>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
            {images.map((image: BuildingImage) => (
              <ImageCard
                key={image.id}
                image={image}
                onDelete={() => handleDelete(image)}
                onSetPrimary={() => handleSetPrimary(image)}
                isDeleting={
                  deleteMutation.isPending &&
                  deleteMutation.variables?.id === image.id
                }
                isSettingPrimary={settingPrimaryId === image.id}
                disabled={disabled || isBusy}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="mt-6 flex flex-col items-center justify-center py-8 text-center">
          <ImageIcon className="h-12 w-12 text-muted-foreground/50" />
          <p className="mt-2 text-sm text-muted-foreground">
            Aun no hay imagenes subidas
          </p>
        </div>
      )}
    </section>
  )
}

// ============================================================================
// Image Card Component
// ============================================================================

interface ImageCardProps {
  image: BuildingImage
  onDelete: () => void
  onSetPrimary: () => void
  isDeleting: boolean
  isSettingPrimary: boolean
  disabled: boolean
}

function ImageCard({
  image,
  onDelete,
  onSetPrimary,
  isDeleting,
  isSettingPrimary,
  disabled,
}: ImageCardProps) {
  return (
    <div className="group relative overflow-hidden rounded-lg border bg-muted">
      {/* Image */}
      <div className="aspect-square">
        <img
          src={image.url}
          alt={image.alt_text || 'Building image'}
          className="h-full w-full object-cover"
          loading="lazy"
        />
      </div>

      {/* Primary Badge */}
      {image.is_primary && (
        <div className="absolute left-2 top-2 flex items-center gap-1 rounded bg-primary px-2 py-1 text-xs font-medium text-primary-foreground">
          <Star className="h-3 w-3 fill-current" />
          Principal
        </div>
      )}

      {/* Overlay with actions */}
      <div
        className={`
          absolute inset-0 flex items-center justify-center gap-2 bg-black/50 transition-opacity
          ${disabled ? 'opacity-0' : 'opacity-0 group-hover:opacity-100'}
        `}
      >
        {/* Set Primary Button */}
        {!image.is_primary && (
          <button
            type="button"
            onClick={onSetPrimary}
            disabled={disabled || isSettingPrimary}
            title="Establecer como principal"
            className="rounded-full bg-white p-2 text-gray-700 shadow-lg transition-transform hover:scale-110 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSettingPrimary ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Star className="h-4 w-4" />
            )}
          </button>
        )}

        {/* Delete Button */}
        <button
          type="button"
          onClick={onDelete}
          disabled={disabled || isDeleting}
          title="Eliminar imagen"
          className="rounded-full bg-red-500 p-2 text-white shadow-lg transition-transform hover:scale-110 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isDeleting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <X className="h-4 w-4" />
          )}
        </button>
      </div>

      {/* File info */}
      <div className="p-2">
        <p className="truncate text-xs text-muted-foreground">
          {image.alt_text || 'Imagen del edificio'}
        </p>
      </div>
    </div>
  )
}
